import { BCMSCurrency } from "@bcm-dev/bcms-vite";
import React, { useEffect, useRef, useState } from "react";

import UIAmountInput from "compid/ui/UIAmountInput";

import { SVGAsset } from 'res';

import "./styles.css";

const CustomSelectContext = React.createContext(null);
/**
 * @name CustomSelect
 * @function
 * @param {STRING|NUMBER|Object|Array} value - what value to set in the input as a default
 * @param {Function} onChange - (STRING|NUMBER->VOID) Call back function for when an Item is selected
 * @param {ReactComponentElement} SelectedItem - ((value)->React.Component) custom element for a selected item

 * @extends React.Component
 * @exported true
 * @description The main component for a custom select
 * @example
 *  let onChange = (value)=>{
 *                  console.log(value)    
 *              }
 *  ...
 *           <CustomSelect value="1"> //isOpen is an optional property
 *             <CustomSelectItem value="1" >Account One</CustomSelectItem>
 *          </CustomSelect>
 *      </div>
 *  );
 * }
 */
export default function CustomAmountSelect(props) {
    const [isActive, setActive] = useState(false);
    const [value, setValue] = useState(props.value);
    const point = props["data-point"] ?? ""; 
    let CustomSelectedItem = props.SelectedItem;
    let selectedRef = useRef();
    // add an index to items for reference to select
    let customItems = <CustomAmountItems bill={props.bill}/>;

    const controller = {
        //compares the value of the item and existing items from prop.children to see which one to pass to selected Item. 
        "selectItem": (item, value) => {
            setValue(BCMSCurrency.toCurrency(value))
            setActive(false)

            if (props.onChange instanceof Function) {
                props.onChange(value);
            }
        },
        "value": () => value,
        "toggle": () => { setActive(!isActive); },
        "close": () => setActive(false),
        "open": () => setActive(true)
    };

    //sets the selectedItem to the DefaultSelectedItem then checks if a valid CustomSelectedItem can be used if true replaces with new view
    let SelectedWrapper = DefaultSelectedItem || undefined;

    SelectedWrapper = ((CustomSelectedItem !== undefined)) ? CustomSelectedItem : SelectedWrapper;
    let selectedIndex = false
    
    //if props.children is only one item it is an object instead of an array
    if (props.children instanceof Array) {
        selectedIndex =  props.children.reduce((result, child, index) => {
            if (result === false) {
                if (isEqual(value, child.props.value || child.props.children)) {
                    return index;
                }
            }
            return result;
        }, false)
    } else {
        if (isEqual(value, props.value || props.children)) {
            selectedIndex = true;
        }
    }

    //rerenders the custom select if the value is set externally
    useEffect(() => setValue(props.value), [ props.value ]);

    //set the selected item.
    let selectedItem =customItems[selectedIndex] || props.children;
    const selectedClass = selectedIndex !== false ? "has-selection" : "";

    return (
        <CustomSelectContext.Provider value={controller}>
          
            <div className={`custom-amount-select ${props.className ?? ""}`} data-point={point}  >
                <UIAmountInput className="amount-input" value={ value } placeHolder="Amount" onChange={ props.onChange } dataPoint={ props.dataPoint }>
                    <span className="sub-text">
                        { props.amountSubText }
                    </span>
                </UIAmountInput>                  
                { (props.bill.isDue === true && props.bill.biller.paymentType !== "paper") && (<SelectedWrapper value={value} controller={controller} > {selectedIndex !== false ? selectedItem : <span className="custom-select-item">&nbsp;</span>} </SelectedWrapper>)}
                {/*The view of the custom select when closed*/}
                {isActive && props.bill.isDue === true && <div className="custom-select-dropdown" > {customItems} </div>}
                {isActive &&  props.bill.isDue === true && <div className="custom-select-overlay" onClick={controller.close}></div>}
            </div>
            
        </CustomSelectContext.Provider>
    );
}

export const useCustomSelect = () => React.useContext(CustomSelectContext);
/**
 * @name CustomSelectItem
 * @function
 * @param {STRING|NUMBER|NULL} value - what value to send to the on change
 * @param {BOOLEAN} disabled -  if the option is not selectable set to true
 * @extends React.Component
 * @exported true
 * @description The component for a custom select item in a CustomSelect component
 * @example
 *  let onChange = (value)=>{
 *                  console.log(value)    
 *              }
 *  ...
 *           <CustomSelect value="1"> //isOpen is an optional property
 *             <CustomSelectItem value="1" >Account One</CustomSelectItem>
 *             <CustomSelectItem value="1" >Account One</CustomSelectItem>
 *          </CustomSelect>
 *      </div>
 *  );
 * }
 */
function CustomAmountItems(props){
    const amountDue = props.bill.amount.amount ?? "200.00";
    const balanceDue = props.bill.balance.amount ?? "1000.00";
    const minimumDue = props.bill.minDue.amount ?? "200.00";
    
    return <>
            <CustomSelectItem value={amountDue}>Amount due {props.bill.amount.format()}</CustomSelectItem>
            <CustomSelectItem value={balanceDue}>Balance due {props.bill.balance.format()}</CustomSelectItem>
            <CustomSelectItem value={minimumDue}>Minimum due {props.bill.minDue.format()}</CustomSelectItem>
            <CustomSelectItem value="0.00">Other amount</CustomSelectItem>
           </>
}

export function CustomSelectItem(props) {
    let ref = useRef();
    const controller = useCustomSelect();

    const onClick = (evt) => {
        if (props.disabled !== true) {
            let selectedItem = React.cloneElement(ref.current, props, props.children);
            controller.selectItem(selectedItem, parseFloat(props.value).toFixed(2) || props.children);
        }
    }
    return (
        <div ref={ref} data-point={ props["data-point"] ? props["data-point"] : ""} className={`${props.disabled ? "disabled " : ""} ${isEqual(controller.value(), props.value || props.children) ? "selected " : ""}custom-select-item ${props.className ?? ""}`} onClick={onClick} >
            {props.children}
        </div>
    )
}

/**
 * @name DefaultSelectedItem
 * @function
 * @param {ARRAY} children - child elements of the selected item 
 * @extends React.Component
 * @exported true
 * @description The component for if a selectedItem view is not passed to customSelect
 */
function DefaultSelectedItem(props) {
    const controller = useCustomSelect();
   
    return (<div className="custom-selected-item" onClick={controller.toggle}>{props.children || <></>}
        <span className="custom-select-button">
            <SVGAsset id="DownChevronIcon" className="custom-select-down" />
        </span>
    </div>
    )
}
function isEqual(valueA, valueB) {
    let reduceObjects = function (a, b) {
        return Object.keys(a).reduce((result, key) => {
            if (result === true) {
                if (a[key] === b[key]) {
                    return true;
                }
                if (a[key] instanceof Object && b[key] instanceof Object) {
                    return reduceObjects(a[key], b[key])
                }
                if (a[key] instanceof Array && b[key] instanceof Array) {
                    return reduceArrays(a[key], b[key])
                }
                return false;
            }
        }, true)
    }
    let reduceArrays = function (a, b) {
        return (a).reduce((result, prop, index) => {
            if (result === true) {
                if (prop === b[index]) {
                    return true;
                }
                if (prop instanceof Object && b[index] instanceof Object) {
                    return reduceObjects(prop, b[index])
                }
                if (prop instanceof Array && b[index] instanceof Array) {
                    return reduceArrays(prop, b[index])
                }
                return false;
            }
        }, true)
    }
    if (valueA === valueB) {
        return true;
    }

    if (valueB.props !== undefined) {
        return valueA == valueB.props.children
    }

    if (valueA instanceof Object && valueB instanceof Object) {
        return reduceObjects(valueA, valueB)
    }

    if (valueA instanceof Array && valueB instanceof Array) {
        return reduceArrays(valueA, valueB)
    }
  
    if (valueA instanceof Function) {      
        return valueA(valueB)
    }
}

